import { useEffect } from "react";
import type { Dispatch, SetStateAction } from "react";
import { WidgetName } from "@src/common/components/Widgets/dashboardWidgetTypes";

interface WidgetListItem {
  name: string;
}

interface UseWidgetShortcutsArgs {
  widgets: ReadonlyArray<WidgetListItem>;
  setSelectedWidget: (name: WidgetName) => void;
  setIsWidgetOpen?: Dispatch<SetStateAction<boolean>>;
  enabled: boolean;
}

export function useWidgetShortcuts({
  widgets,
  setSelectedWidget,
  setIsWidgetOpen,
  enabled,
}: UseWidgetShortcutsArgs) {
  useEffect(() => {
    if (!enabled) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }
      const active = document.activeElement as HTMLElement | null;
      if (
        active &&
        (["INPUT", "TEXTAREA", "SELECT"].includes(active.tagName) ||
          active.isContentEditable)
      ) {
        return;
      }
      if (document.querySelector('[data-baseweb="modal"]')) {
        return;
      }

      if (event.key === "\\") {
        if (setIsWidgetOpen) setIsWidgetOpen((prev) => !prev);
        return;
      }

      const num = parseInt(event.key, 10);
      if (Number.isInteger(num) && num >= 1 && num <= widgets.length) {
        const widget = widgets[num - 1];
        setSelectedWidget(widget.name as WidgetName);
        if (setIsWidgetOpen) setIsWidgetOpen(true);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [enabled, widgets, setSelectedWidget, setIsWidgetOpen]);
}
